import { Profile } from '../types/auth.types';
import { createClient } from '../supabase/client';
import { get, patch } from '../api/rest-client';
import { unauthenticatedFetch } from '../api/base-client';

// Consistent cookie name with middleware - This is now handled by Supabase @supabase/ssr
// const SESSION_COOKIE_NAME = "pear_session_active"

interface LoginResponse {
  access_token: string;
  profile: Profile;
}

class AuthService {
  private static instance: AuthService;
  private currentUser: Profile | null = null;
  private supabase = createClient();

  private constructor() {
    // No need for initial state loading from localStorage here for auth tokens
    // Supabase client handles session restoration
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Public method to get the Supabase user object
  async getSupabaseUser() {
    return this.supabase.auth.getUser();
  }

  async getAccessToken(): Promise<string | null> {
    const {
      data: { session },
    } = await this.supabase.auth.getSession();
    return session?.access_token ?? null;
  }

  getProfile(): Profile | null {
    return this.currentUser;
  }

  async isAuthenticated(): Promise<boolean> {
    const {
      data: { session },
    } = await this.supabase.auth.getSession();
    return !!session;
  }

  // Helper to fetch and set full profile data, including isStaff
  private async fetchAndSetCurrentUser(userId: string): Promise<Profile | null> {
    try {
      const { data: profileData } = await get<{ data: Profile }>(`/profiles/${userId}`);
      if (profileData) {
        this.currentUser = profileData;
        return profileData;
      }
      this.currentUser = null;
      return null;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      this.currentUser = null;
      return null;
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const { data: authData, error: signInError } = await this.supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      throw signInError;
    }
    if (!authData.user || !authData.session) {
      throw new Error('Login failed: No user or session data returned.');
    }

    const profile = await this.fetchAndSetCurrentUser(authData.user.id);

    if (!profile || !profile.isStaff) {
      await this.supabase.auth.signOut(); // Sign out if not staff
      this.currentUser = null;
      throw new Error('User is not authorized or profile not found.');
    }

    return {
      access_token: authData.session.access_token,
      profile: this.currentUser!,
    };
  }

  async signup(payload: { email: string; password: string; firstName: string; lastName: string }): Promise<Profile> {
    const { data: signUpData, error: signUpError } = await this.supabase.auth.signUp({
      email: payload.email,
      password: payload.password,
      options: {
        data: {
          // This data is typically stored in user_metadata or app_metadata
          firstName: payload.firstName,
          lastName: payload.lastName,
        },
      },
    });

    if (signUpError) {
      throw signUpError;
    }
    if (!signUpData.user) {
      throw new Error('Signup did not return a user.');
    }

    // After Supabase user creation, the application might need to create a
    // corresponding entry in a public 'profiles' table. This could be handled
    // by a database trigger, a Supabase Function, or an explicit API call here.
    // For now, we'll attempt to fetch the profile, assuming it might be created.
    // A more robust solution might involve an explicit call to create the profile
    // if it doesn't exist after signup.

    // Try to fetch the profile. It should exist if a trigger/function created it.
    // isStaff will typically be false by default for new signups.
    const profile = await this.fetchAndSetCurrentUser(signUpData.user.id);

    if (profile) {
      // If signup results in an active session (e.g., email confirmation is off)
      // this.currentUser would be set.
      return profile;
    } else {
      // Fallback: If profile couldn't be fetched (e.g., not yet created by a trigger)
      // return a synthesized profile. The `isStaff` status would be unknown or default.
      // This part might need adjustment based on how profiles are provisioned.
      console.warn(
        `Profile for ${signUpData.user.id} not found immediately after signup. Ensure profile creation process.`,
      );
      const synthesizedProfile: Profile = {
        id: signUpData.user.id,
        email: signUpData.user.email!,
        firstName: payload.firstName,
        lastName: payload.lastName,
        isStaff: false, // Default assumption
      };
      if (signUpData.session) {
        // If Supabase created a session
        this.currentUser = synthesizedProfile; // Set basic profile
      }
      return synthesizedProfile;
    }
  }

  forgotPassword(email: string): Promise<string> {
    const url = '/profiles/forgot-password';
    const rootUrl = window.location.origin;
    return unauthenticatedFetch(url, {
      method: 'POST',
      body: JSON.stringify({ email, rootUrl }),
    }).then((response) => response.json());
  }

  resetPasswordFromEmail(email: string, newPassword: string): Promise<string> {
    const url = '/profiles/reset-password';
    return unauthenticatedFetch(url, {
      method: 'POST',
      body: JSON.stringify({ email, newPassword }),
    }).then((response) => response.json());
  }

  resetPassword(oldPassword: string, newPassword: string): Promise<string> {
    const url = '/profiles/me/change-password';
    return patch(url, {
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  async verifySession(): Promise<boolean> {
    const {
      data: { session },
      error: sessionError,
    } = await this.supabase.auth.getSession();

    if (sessionError || !session || !session.user) {
      // Clear local state and redirect if session is invalid
      this.currentUser = null;
      // No need to call clearSessionData explicitly, logout will handle it if needed
      // or rely on page guard to redirect. For verifySession, just return false.
      // If this is called on app load, a redirect might be handled by a route guard.
      return false;
    }

    const profile = await this.fetchAndSetCurrentUser(session.user.id);

    if (!profile || !profile.isStaff) {
      // If user is authenticated with Supabase but not a staff member or profile missing,
      // treat as invalid session for this app's purpose.
      await this.supabase.auth.signOut(); // Sign out the Supabase session
      this.currentUser = null;
      return false;
    }

    // currentUser is now set and verified
    return true;
  }

  async logout() {
    const { error } = await this.supabase.auth.signOut();
    this.currentUser = null;
    if (error) {
      console.error('Error logging out:', error);
      // Decide on error handling, but still attempt to redirect
    }
    // Redirect after clearing data
    window.location.href = '/login';
  }
}

export const authService = AuthService.getInstance();
